import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Suspense,
  useMemo,
} from "react";
import styled from "styled-components";
import { useDownloadBanner } from "@agir/front/app/hooks/useDownloadBanner";
import { lazy } from "@agir/front/app/utils";

const RSBS = lazy(() => import("./ReactSpringBottomSheet"));

const StyledBottomSheetFooter = styled.footer`
  &::before {
    content: "";
    display: block;
    width: calc(100% - 3rem);
    height: 1px;
    margin: 0 auto;
    background-color: ${(props) => props.theme.text200};
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.text1000};
    background-color: ${(props) => props.theme.background0};
    box-shadow: none;
    border: none;
    height: 54px;
    font-size: 0.875rem;
    width: 100%;
    cursor: pointer;
  }
`;
const StyledBottomSheet = styled(RSBS)`
  [data-rsbs-overlay] {
    background-color: ${(props) => props.theme.background0};
    z-index: ${(props) => props.theme.zindexPanel};
  }
  [data-rsbs-backdrop] {
    z-index: ${(props) => props.theme.zindexPanel};
    background-color: rgba(0, 10, 44, 0.6);
  }
  [data-rsbs-footer] {
    padding: 0;
    box-shadow: none;
  }
`;

export const BottomSheet = (props) => {
  const {
    isOpen,
    onDismiss,
    shouldDismissOnClick,
    hideClose = false,
    children,
  } = props;
  const [isReady, setIsReady] = useState(false);
  const contentRef = useRef();
  const [hasBanner] = useDownloadBanner();

  const getUsedSpaceOnTop = useCallback(() => {
    const style = window.getComputedStyle(document.body);
    const topBarHeightRaw = style.getPropertyValue("--top-bar-height");
    const downloadAppBannerHeightRaw = style.getPropertyValue(
      "--download-app-banner-height",
    );

    const toInt = (s) => {
      const toIntRgx = RegExp(/\d+/);
      const matches = s.match(toIntRgx);
      if (matches && Array.isArray(matches) && matches.length)
        return Number(matches[0]);
      return 0;
    };

    const topBarHeight = toInt(topBarHeightRaw);
    const downloadAppBannerHeight = hasBanner
      ? toInt(downloadAppBannerHeightRaw)
      : 0;

    return topBarHeight + downloadAppBannerHeight;
  }, [hasBanner]);

  const usedSpaceAtTop = useMemo(
    () => getUsedSpaceOnTop(),
    [getUsedSpaceOnTop],
  );

  const handleSpringStart = useCallback(
    (e) => {
      let content = contentRef.current;
      if (!content || !shouldDismissOnClick) {
        return;
      }
      const targets = content.querySelectorAll("a, button");
      if (e.type === "OPEN") {
        targets.forEach((element) => {
          element.addEventListener("click", onDismiss);
        });
      }
      if (e.type === "CLOSE") {
        targets.forEach((element) => {
          element.removeEventListener("click", onDismiss);
        });
      }
    },
    [onDismiss, shouldDismissOnClick],
  );

  useEffect(() => {
    isOpen && !isReady && setIsReady(true);
  }, [isOpen, isReady]);

  return (
    <Suspense fallback={null}>
      {isReady && (
        <StyledBottomSheet
          open={isOpen}
          onDismiss={onDismiss}
          onSpringStart={handleSpringStart}
          defaultSnap={({ maxHeight }) => {
            let maxSnapPoint = maxHeight - usedSpaceAtTop - 16;
            return maxSnapPoint;
          }}
          snapPoints={({ maxHeight }) => {
            let maxSnapPoint = maxHeight - usedSpaceAtTop - 16;
            return [maxSnapPoint / 3, 2 * (maxSnapPoint / 3), maxSnapPoint];
          }}
          footer={
            hideClose ? null : (
              <StyledBottomSheetFooter>
                <button onClick={onDismiss}>Fermer</button>
              </StyledBottomSheetFooter>
            )
          }
        >
          <div ref={contentRef}>{children}</div>
        </StyledBottomSheet>
      )}
    </Suspense>
  );
};

BottomSheet.propTypes = {
  isOpen: PropTypes.bool,
  onDismiss: PropTypes.func,
  shouldDismissOnClick: PropTypes.bool,
  hideClose: PropTypes.bool,
  children: PropTypes.node,
};
export default BottomSheet;
